import { Numeric } from "../util/Numeric";
import { AccountingData, ExportMethod, LedgerEntry } from "../model";
import { Dates } from "../util/Dates";
import { Translations } from "../logic/Translations";

/** TargetOption */
interface TargetOptions {
  [key: string]: TargetOption;
}

/** TargetOption */
interface TargetOption {
  /** Export function */
  getExportData?: (accountingData: AccountingData, schemeId?: string) => any[];

  /** List of supported schemes */
  schemes?: { id: string, label: string }[],

  /** Default export method */
  defaultExportMethod?: string;
  /** Boolean indicating if the csv export should contain quoted strings */
  noCsvQuotedStrings?: boolean;

  /** Boolean indicating if the csv export should contain byte order mark */
  noCsvBom?: boolean;

  /** Format data as text*/
  formatAsText?: (data: any[], isFinnish: boolean) => string;
}

/**
 * Provides business logic to AccountingReportTools
 */
export class AccountingTargetLogic {


  /**
   * Get supported TargetOptions
   * @returns All supported export TargetOptions
   */
  public static getSupportedTargets(): TargetOptions {
    return {
      // Palkkaus.fi
      "36dff2e2-e181-4f0d-9004-0e4aa92f5e17":
      {
        getExportData: (accountingData: AccountingData, schemeId: string) => {
          return this.getPalkkausExportData(accountingData, schemeId);
        },
        schemes: [
          {
            id: "default",
            label: Translations.get("SALAXY.NG1.AccountingReportToolsComponent.schemes.default.label"),
          },
          {
            id: "compact",
            label: Translations.get("SALAXY.NG1.AccountingReportToolsComponent.schemes.compact.label"),
          },
          {
            id: "extended",
            label: Translations.get("SALAXY.NG1.AccountingReportToolsComponent.schemes.extended.label"),
          },
        ],
        defaultExportMethod: ExportMethod.FileExcel,
      },
      // Palkkaus.fi ja lomat
      "8fd494ba-f98d-45e2-ae70-3570e16dfa3c":
      {
        getExportData: (accountingData: AccountingData, schemeId: string) => {
          return this.getPalkkausExportData(accountingData, schemeId);
        },
        schemes: [
          {
            id: "default",
            label: Translations.get("SALAXY.NG1.AccountingReportToolsComponent.schemes.default.label"),
          },
          {
            id: "compact",
            label: Translations.get("SALAXY.NG1.AccountingReportToolsComponent.schemes.compact.label"),
          },
          {
            id: "extended",
            label: Translations.get("SALAXY.NG1.AccountingReportToolsComponent.schemes.extended.label"),
          },
        ],
        defaultExportMethod: ExportMethod.FileExcel,
      },
      // Fennoa
      "0db4e85f-93a8-46f1-b6d2-f7523d289d88":
      {
        getExportData: (accountingData: AccountingData) => {
          return this.getFennoaExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.FileCsv,
      },
      // Finago Procountor
      "8613f133-3fce-4611-b372-51f6a2e007dd":
      {
        getExportData: (accountingData: AccountingData) => {
          return this.getProcountorExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.CopyExcel,
      },
      // Accountor Go
      "4b3335ad-dfd2-48f5-a088-6449874a0732":
      {
        getExportData: (accountingData: AccountingData) => {
          return this.getProcountorExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.CopyExcel,
      },
      // MeritAktiva
      "e62c10b0-222a-4170-b8b4-b1e02d9a7644":
      {
        getExportData: (accountingData: AccountingData) => {
          return this.getMeritAktivaExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.FileCsv,
        noCsvQuotedStrings: true,
      },
      // Heeros
      "53fd3863-b8f9-4909-86c9-cb4597595181":
      {
        getExportData: (accountingData: AccountingData, schemeId: string) => {
          return this.getHeerosExportData(accountingData, schemeId);
        },
        defaultExportMethod: ExportMethod.FileCsv,
        noCsvQuotedStrings: true,
        schemes: [
          {
            id: "default",
            label: Translations.get("SALAXY.NG1.AccountingReportToolsComponent.schemes.default.label"),
          },
          {
            id: "extended",
            label: Translations.get("SALAXY.NG1.AccountingReportToolsComponent.schemes.extended.label"),
          },
        ],
      },
      // Asteri
      "2fc7899c-7a3d-4d56-ad2f-05d52782bbbc":
      {
        getExportData: (accountingData: AccountingData) => {
          return this.getAsteriExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.CopyExcel
      },
      // Maestro
      "0a2bd312-06da-4862-aef5-75166e0bf461":
      {
        getExportData: (accountingData: AccountingData) => {
          return this.getMaestroExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.FileCsv,
        noCsvQuotedStrings: true,
      },
      // Visma Fivaldi
      "c04e0757-63f3-420d-a183-3427710fc954":
      {
        getExportData: (accountingData: AccountingData) => {
          return this.getVismaFivaldiExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.FileCsv,
        noCsvBom: true
      },
      // Netvisor
      "ba271dac-dd63-4036-9d70-053a09ca4d9e":
      {
        getExportData: (accountingData: AccountingData) => {
          return this.getVismaNetvisorExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.FileCsv,
      },
      // Lemonsoft
      "27e271d5-41f5-4319-ab29-4d5d9eb945c6":
      {
        getExportData: (accountingData: AccountingData) => {
          return this.getLemonsoftExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.CopyExcel
      },
      // Tampuuri
      "d311f9d3-8c3a-4fd8-a665-8e3698e41e87": {
        getExportData: (accountingData: AccountingData) => {
          return this.getTampuuriExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.CopyExcel
      },
      // Domus
      "d58e95b7-d0b5-40ac-992b-6d3b29efc45e":
      {
        getExportData: (accountingData: AccountingData) => {
          return this.getDomusExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.FileCsv,
      },
      //Oscar Software
      "54e93c5f-bd74-4157-8a74-3515be7f585d": {
        getExportData: (accountingData: AccountingData) => {
          return this.getOscarSoftwareExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.FileExcel,
        noCsvQuotedStrings: true,
      },
      //Digia Enterprise / Envision
      "0e4f0a71-dcb9-43ba-9028-0239e674711f": {
        getExportData: (accountingData: AccountingData) => {
          return this.getDigiaExportData(accountingData);
        },
        defaultExportMethod: ExportMethod.FileText,
        noCsvQuotedStrings: true,
        formatAsText: (data: any[], isFinnish: boolean) => {
          return this.formatAsDigiaText(data, isFinnish);
        }
      }
    };
  }

  private static getPalkkausExportData(data: AccountingData, schemeId?: any): any[] {
    if (schemeId === "extended" || schemeId === "compact") {
      return this.getPalkkausExtendedExportData(data, schemeId == "extended");
    }

    return this.getPalkkausDefaultExportData(data);
  }

  /** Data exports for targets */
  private static getPalkkausDefaultExportData(data: AccountingData): any[] {
    const result = [];
    const header = ["Ehdotustili", "Selite", "ALV-%"];
    const dimensionHeaders = [];
    const addToGlobal = (value, name) => {
      if (value) {
        if (!dimensionHeaders.includes(name) && name !== "costCenter") {
          dimensionHeaders.push(name);
          header.push(name);
        }
      }
    };
    // Find all dimensions
    for (const rowData of data.ledgerAccounts) {
      for (const entryData of rowData.entries) {
        const costCenter = entryData.dimension.costCenter || entryData.dimension.dimensions?.costCenter;
        addToGlobal(costCenter, "Kustannuspaikka");
        const projectNumber = entryData.dimension.projectNumber || entryData.dimension.dimensions?.projectNumber;
        addToGlobal(projectNumber, "projectNumber");
        if (entryData.dimension.dimensions) {
          for (const [key, value] of Object.entries(entryData.dimension.dimensions)) {
            addToGlobal(value, key);
          }
        }

      }
    }
    header.push("Saldo");
    result.push(header);

    for (const rowData of data.ledgerAccounts) {
      const keyEntriesRows: {
        /** Entries */
        entries: LedgerEntry[],
        /** VAT */
        vatPercent: number,
        /** Dimensions */
        dimensions: any[],
        /** DimensionsId */
        dimensionsId: string
      }[] = [];
      for (const entryData of rowData.entries) {
        const rowDataEntriesDimensions = [];
        for (const dimensionHeader of dimensionHeaders) {
          if (dimensionHeader === "Kustannuspaikka") {
            const costCenter = entryData.dimension.costCenter || entryData.dimension.dimensions?.costCenter || "";
            rowDataEntriesDimensions.push(costCenter);
          }
          else if (dimensionHeader === "projectNumber") {
            const projectNumber = entryData.dimension.projectNumber || entryData.dimension.dimensions?.projectNumber || "";
            rowDataEntriesDimensions.push(projectNumber);
          } else {
            rowDataEntriesDimensions.push(entryData.dimension.dimensions ? (entryData.dimension.dimensions[dimensionHeader] || "") : "");
          }
        }
        const vatPercent = entryData.vatPercent ?? 0;
        let keyEntryRows: {
          /** Entries */
          entries: LedgerEntry[],
          /** VAT */
          vatPercent: number,
          /** Dimensions */
          dimensions: any[],
          /** DimensionsId */
          dimensionsId: string
        } = keyEntriesRows.find((x) => x.vatPercent === vatPercent && x.dimensionsId === rowDataEntriesDimensions.join("-"));
        if (keyEntryRows == null) {
          keyEntryRows = { entries: [], vatPercent, dimensions: rowDataEntriesDimensions, dimensionsId: rowDataEntriesDimensions.join("-") };
          keyEntriesRows.push(keyEntryRows);
        }
        keyEntryRows.entries.push(entryData);
      }
      // Append results
      for (const keyEntryRows of keyEntriesRows) {
        const row = [];
        row.push(rowData.accountNumber);
        row.push(rowData.accountName);
        row.push(keyEntryRows.vatPercent * 100);
        for (let i = 0; i < dimensionHeaders.length; i++) {
          row.push(keyEntryRows.dimensions[i]);
        }
        const saldo = Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0));
        row.push(saldo);
        result.push(row);
      }
    }
    return result;
  }

  private static getPalkkausExtendedExportData(data: AccountingData, isExtended: boolean): any[] {
    const result = [];
    const header = [];

    const accountKeys = ["accountNumber", "saldo", "accountName"];
    const accountKeyNames = ["Tilinumero", "Summa", "Tilin nimi"];
    const entryKeys = ["description", "isDebit", "amount", "vatPercent"];
    const entryKeyNames = ["Kuvaus", "Debet", "Arvo", "ALV-prosentti"];
    const dimensionKeys = ["calculationOwner", "calculationId", "calculationTitle", "projectNumber", "costCenter", "workerName"];
    const dimensionKeyNames = ["Laskelman tili", "Laskelman id", "Laskelman kuvaus", "Projektinumero", "Kustannuspaikka", "Työntekijä"];

    for (const accountKeyName of accountKeyNames) {
      header.push(accountKeyName);
    }
    if (isExtended) {
      for (const entryKeyName of entryKeyNames) {
        header.push(entryKeyName);
      }
      for (const dimensionKeyName of dimensionKeyNames) {
        header.push(dimensionKeyName);
      }
    }
    result.push(header);
    for (const rowData of data.ledgerAccounts) {
      let row = [];
      for (const accountKey of accountKeys) {
        switch(accountKey) {
          case "saldo":
            row.push(Numeric.round(rowData[accountKey]));
            break;
          default:
            row.push(rowData[accountKey]);
            break;
        }
      }
      if (isExtended) {
        let first = true;
        for (const entryData of rowData.entries) {
          if (first) {
            first = false;
          } else {
            row = new Array(accountKeys.length);
          }
          for (const entryKey of entryKeys) {
            switch(entryKey) {
              case "vatPercent":
                row.push(entryData[entryKey] * 100);
                break;
              case "amount":
                row.push(Numeric.round(entryData[entryKey]));
                break;
              default:
                row.push(entryData[entryKey]);
                break;
            }
          }
          for (const dimensionKey of dimensionKeys) {
            if (dimensionKey === "costCenter") {
              row.push(entryData.dimension.costCenter || entryData.dimension.dimensions?.costCenter);
            }
            else if (dimensionKey == "projectNumber") {
              row.push(entryData.dimension.projectNumber || entryData.dimension.dimensions?.projectNumber);
            }
            else {
              row.push(entryData.dimension[dimensionKey]);
            }
          }
          result.push(row);
        }
      } else {
        result.push(row);
      }
    }
    return result;
  }

  /**
   * https://procountor.finago.com/hc/fi/articles/360000254997-Tallennusty%C3%B6kalu-uusi-#LIIT%C3%84VIENTEJ%C3%84-TOIMINTO
   */
  private static getProcountorExportData(data: AccountingData): any[] {
    const result = [];

    const header = ["Kp-tili", "Brutto", "ALV-%", "ALV-väh.%", "ALV-tyyppi", "ALV-status", "Vientiselite"];
    result.push(header);

    for (const rowData of data.ledgerAccounts) {
      const keyEntriesRows: {
        /** Vat key */
        vatPercent: number,
        /** Ledger entries */
        entries: LedgerEntry[],
      }[] = [];
      for (const entryData of rowData.entries) {
        const vatPercent = entryData.vatPercent ?? 0;
        let keyEntryRows: {
          /** Vat key */
          vatPercent: number,
          /** Ledger entries */
          entries: LedgerEntry[],
        } = keyEntriesRows.find((x) => x.vatPercent === vatPercent);
        if (keyEntryRows == null) {
          keyEntryRows = { vatPercent, entries: [] };
          keyEntriesRows.push(keyEntryRows);
        }
        keyEntryRows.entries.push(entryData);
      }
      // Append results
      for (const keyEntryRows of keyEntriesRows) {
        const row = [];
        // "Kp-tili",
        row.push(rowData.accountNumber);
        // "Brutto",
        row.push(Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0)));
        // "ALV-%",
        row.push(keyEntryRows.vatPercent ? (keyEntryRows.vatPercent * 100) : "");
        // "ALV-väh.%",
        row.push(keyEntryRows.vatPercent ? 100 : "");
        // "ALV-tyyppi",
        row.push(keyEntryRows.vatPercent ? "P" : "");
        // "ALV-status",
        row.push(keyEntryRows.vatPercent ? "vat_1" : "");
        // "Vientiselite"
        row.push(rowData.accountName);

        result.push(row);
      }
    }
    return result;
  }

  /**
   * Merit aktiva format
   */
  private static getMeritAktivaExportData(data: AccountingData): any[] {
    const result = [];
    const header = ["T", data.officialId, data.employer.displayName, Dates.asJSDate(data.period.end), null, Dates.asJSDate(data.period.start), Dates.asJSDate(data.period.end)];
    result.push(header);

    for (const rowData of data.ledgerAccounts) {
      const row = [];
      row.push("V");
      row.push(rowData.accountNumber);
      row.push(rowData.accountName);
      row.push(Numeric.round(rowData.saldo));
      row.push(null);
      row.push(null);
      row.push(null);

      result.push(row);
    }
    return result;
  }

  /**
   * https://asteri.fi/aineistojen-tuonti-asteri-kirjanpitoon/
   */
  private static getAsteriExportData(data: AccountingData): any[] {

    const result = [];

    const header = ["Tosno", "Päivämäärä", "Laji", "Kustp", "Lsno", "Selite 1", "Selite 2", "Debet", "Kredit", "Summa"];
    result.push(header);

    const entryDate = this.getEntryDate(data);

    for (const rowData of data.ledgerAccounts) {
      const keyEntriesRows: {
        /** Cost center */
        costCenter: string,
        /** Ledger entries */
        entries: LedgerEntry[],
      }[] = [];
      for (const entryData of rowData.entries) {
        const costCenter = entryData.dimension.costCenter || entryData.dimension.dimensions?.costCenter || "";
        let keyEntryRows: {
          /** Cost center */
          costCenter: string,
          /** Ledger entries */
          entries: LedgerEntry[],
        } = keyEntriesRows.find((x) => x.costCenter === costCenter);
        if (keyEntryRows == null) {
          keyEntryRows = { costCenter, entries: [] };
          keyEntriesRows.push(keyEntryRows);
        }
        keyEntryRows.entries.push(entryData);
      }
      // Append results
      for (const keyEntryRows of keyEntriesRows) {
        const row = [];

        // "Tosno",
        row.push(null);
        // "Päivämäärä",
        row.push(Dates.asJSDate(entryDate));
        // "Laji",
        row.push("Palkkaus");
        // "Kustannuspaikka",
        row.push(keyEntryRows.costCenter);
        // "Laskunnumero",
        row.push(null);
        // "Selite1",
        row.push(rowData.accountName);
        // "Selite2",
        row.push(null);

        const saldo = Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0));
        if (saldo >= 0) {
          // "Debet-tili",
          row.push(rowData.accountNumber);
          // "Kredit-tili",
          row.push("****");
          // "Summa"
          row.push(saldo);
        } else {
          // "Debet-tili",
          row.push("****");
          // "Kredit-tili",
          row.push(rowData.accountNumber);
          // "Summa"
          row.push(saldo * -1.0);
        }
        result.push(row);
      }
    }
    return result;
  }

  /**
   * Maestro
   * +----------+--------------+-----------------------------------------+
   * | Otsikko  |    Muoto     |                 Selite                  |
   * +----------+--------------+-----------------------------------------+
   * | TOSITE   | VVVV/L/NNNNN | Tositenumero                            |
   * | PAIVAYS  | PP.KK.VVVV   | Kirjauspäivä                            |
   * | ERN      | NUM          | Erityisraporttinro                      |
   * | KAAVA    | NUM          | Tulos/Tasekaavanumero                   |
   * | TNRO     | NUM          | Tilinumero                              |
   * | RNRO     |              | Raporttinumero                          |
   * | TILINIMI | AN           | Tilin Nimi                              |
   * | TUPA     | NUM          | Tulospaikkanumero                       |
   * | ATP      |              | Aputulospaikkanumero                    |
   * | SELITE   | AN           | Vientiselite                            |
   * | VARA1    |              | Varalla/1                               |
   * | VARA2    |              | Varalla/2                               |
   * | VARA3    |              | Varalla/3                               |
   * | VARA4    |              | Varalla/4                               |
   * | SUMMA    |              | Summa (2-desimaalisena erotin on piste) |
   * +----------+--------------+-----------------------------------------+
   */
  private static getMaestroExportData(data: AccountingData): any[] {

    const result = [];

    const header = ["TOSITE", "PAIVAYS", "ERN", "KAAVA", "TNRO", "RNRO", "TILINIMI", "TUPA", "ATP", "SELITE", "VARA1", "VARA2", "VARA3", "VARA4", "SUMMA"];
    result.push(header);

    const entryDate = this.getEntryDate(data);

    for (const rowData of data.ledgerAccounts) {
      const keyEntriesRows: {
        /** Cost center */
        costCenter: string,
        /** Ledger entries */
        entries: LedgerEntry[],
      }[] = [];
      for (const entryData of rowData.entries) {
        const costCenter = entryData.dimension.costCenter || entryData.dimension.dimensions?.costCenter || "";
        let keyEntryRows: {
          /** Cost center */
          costCenter: string,
          /** Ledger entries */
          entries: LedgerEntry[],
        } = keyEntriesRows.find((x) => x.costCenter === costCenter);
        if (keyEntryRows == null) {
          keyEntryRows = { costCenter, entries: [] };
          keyEntriesRows.push(keyEntryRows);
        }
        keyEntryRows.entries.push(entryData);
      }
      // Append results
      for (const keyEntryRows of keyEntriesRows) {
        const row = [];

        const saldo = Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0));

        // TOSITE,
        row.push(null);
        // PAIVAYS,
        row.push(Dates.asJSDate(entryDate));
        // ERN
        row.push(null);
        // KAAVA
        row.push(null);
        // TNRO
        row.push(Numeric.parseNumber(rowData.accountNumber));
        // RNRO
        row.push(null);
        // TILINIMI
        row.push(rowData.accountName);
        // TUPA
        row.push(null);
        // ATP
        row.push(keyEntryRows.costCenter);
        // SELITE
        row.push(rowData.accountName);
        // VARA1
        row.push(null);
        // VARA2
        row.push(null);
        // VARA3
        row.push(null);
        // VARA4
        row.push(null);
        // SUMMA
        row.push(saldo);

        result.push(row);
      }
    }
    return result;
  }

  /**
   * https://support.netvisor.fi/hc/fi/articles/235300547-Kirjanpitotietojen-tuonti
   */
  private static getVismaNetvisorExportData(data: AccountingData): any[] {
    const result = [];

    const header = [
      "TositeLaji",
      "Tositenumero",
      "Päivämäärä (PVM)",
      "Selite 1",
      "Selite 2",
      "Selite 3",
      "Selite 4",
      "Selite 5",
      "Tilinumero",
      "Debet",
      "Kredit",
      "Summa",
      "Summa",
      "ALV-tunnus",
      "ALV-prosentti",
    ];
    result.push(header);

    const entryDate = this.getEntryDate(data);

    for (const rowData of data.ledgerAccounts) {
      const keyEntriesRows: {
        /** Vat key */
        vatPercent: number,
        /** Ledger entries */
        entries: LedgerEntry[],
      }[] = [];
      for (const entryData of rowData.entries) {
        const vatPercent = entryData.vatPercent ?? 0;
        let keyEntryRows: {
          /** Vat key */
          vatPercent: number,
          /** Ledger entries */
          entries: LedgerEntry[],
        } = keyEntriesRows.find((x) => x.vatPercent === vatPercent);
        if (keyEntryRows == null) {
          keyEntryRows = { vatPercent, entries: [] };
          keyEntriesRows.push(keyEntryRows);
        }
        keyEntryRows.entries.push(entryData);
      }
      // Append results
      for (const keyEntryRows of keyEntriesRows) {
        const row = [];
        // "TositeLaji",
        row.push(null);
        // "Tositenumero",
        row.push(null);
        // "Päivämäärä (PVM)",
        row.push(Dates.asJSDate(entryDate));
        // "Selite 1",
        row.push(rowData.accountName);
        // "Selite 2",
        row.push(null);
        // "Selite 3",
        row.push(null);
        // "Selite 4",
        row.push(null);
        // "Selite 5",
        row.push(null);
        // "Tilinumero",
        row.push(rowData.accountNumber);

        const saldo = Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0));
        if (saldo >= 0) {
          // "Debet",
          row.push(saldo);
          // "Kredit",
          row.push(0);
          // "Summa",
          row.push(saldo);
          // "Summa",
          row.push(saldo * -1.0);
        } else {
          // "Debet",
          row.push(0);
          // "Kredit",
          row.push(saldo * -1.0);
          // "Summa",
          row.push(saldo);
          // "Summa",
          row.push(saldo * -1.0);
        }
        // "ALV-tunnus",
        row.push(keyEntryRows.vatPercent ? "KOOS" : "");
        // "ALV-prosentti",
        row.push(keyEntryRows.vatPercent ? (keyEntryRows.vatPercent * 100) : "");
        result.push(row);
      }
    }
    return result;
  }

  /**
   * Fennoa export data format.
   * Tuetut sarakkeet:
   *
   * - PVM - Tositepäivä. Ensimmäiseltä riviltä otetaan Fennoan tositteelle päivämäärä.
   * - TILI - Kirjanpidon tili
   * - SELITE - Vientirivin selite
   * - SUMMA (Debet +, Kredit -)
   * - ALV-KOODI
   * - tyhjä tarkoittaa tilikartasta tilin takaa haetaan oletus alv-koodi
   * - viiva ”-” tarkoittaa ei alv-koodia
   * - 1 = 24
   * - 2 = 14
   * - 3 = 10
   * - numero on vientiriville halutun alv-koodin id numero, jotka löytyvät ohjekannastamme ALV-kooditaulukosta (3.9.5 ALV-koodit / Arvonlisäverokoodit Fennoassa).
   * - LK1 - Laskentakohde 1
   * - LK2 - Laskentakohde 2
   * - LK… jne
   */
  private static getFennoaExportData(data: AccountingData): any[] {
    const result = [];

    const header = [
      "PVM",
      "TILI",
      "SELITE",
      "SUMMA",
      "ALV-KOODI",
      "LK1",
    ];
    result.push(header);

    const entryDate = this.getEntryDate(data);

    for (const rowData of data.ledgerAccounts) {
      const keyEntriesRows: {
        /** Vat key */
        vatPercent: number,
        /** Cost center key */
        costCenter: string,
        /** Ledger entries */
        entries: LedgerEntry[],
      }[] = [];
      for (const entryData of rowData.entries) {
        const vatPercent = entryData.vatPercent ?? 0;
        const costCenter = entryData.dimension.costCenter || entryData.dimension.dimensions?.costCenter || "";
        let keyEntryRows: {
          /** Vat key */
          vatPercent: number,
          /** Cost center key */
          costCenter: string,
          /** Ledger entries */
          entries: LedgerEntry[],
        } = keyEntriesRows.find((x) => x.vatPercent === vatPercent && x.costCenter === costCenter);
        if (keyEntryRows == null) {
          keyEntryRows = { vatPercent, costCenter, entries: [] };
          keyEntriesRows.push(keyEntryRows);
        }
        keyEntryRows.entries.push(entryData);
      }
      // Append results
      for (const keyEntryRows of keyEntriesRows) {
        const row = [];
        // PVM
        row.push(Dates.asJSDate(entryDate));
        // TILI
        row.push(rowData.accountNumber);
        // SELITE,
        row.push(rowData.accountName);
        const saldo = Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0));
        // SUMMA
        row.push(saldo);
        // ALV-KOODI
        row.push(keyEntryRows.vatPercent ?
          ((keyEntryRows.vatPercent * 100 === 24 || keyEntryRows.vatPercent * 100 === 25.5) ? 1 :
            (keyEntryRows.vatPercent * 100 === 14) ? 2 :
              (keyEntryRows.vatPercent * 100 === 10) ? 3 :
                "") : "");
        // LK
        row.push(keyEntryRows.costCenter);

        result.push(row);
      }
    }
    return result;
  }

  /**
   * Domus export data format.
   * Tuetut sarakkeet:
   * - Y-TUNNUS - Yrityksen y-tunnus
   * - PVM - Tositepäivä. Ensimmäiseltä riviltä otetaan Domus tositteelle päivämäärä.
   * - TILI - Kirjanpidon tili
   * - SELITE - Vientirivin selite
   * - SUMMA (Debet +, Kredit -)
   */
  private static getDomusExportData(data: AccountingData): any[] {
    const result = [];

    const header = [
      "Y-TUNNUS",
      "PVM",
      "TILI",
      "SELITE",
      "SUMMA",
    ];
    result.push(header);

    const entryDate = this.getEntryDate(data);

    for (const rowData of data.ledgerAccounts) {
        const row = [];
        // Y-TUNNUS
        row.push(data.officialId);
        // PVM
        row.push(Dates.asJSDate(entryDate));
        // TILI
        row.push(rowData.accountNumber);
        // SELITE,
        row.push(rowData.accountName);
        // SUMMA
        row.push(Numeric.round(rowData.saldo));
        result.push(row);
    }
    return result;
  }

  /**
   * Oscar Software
   * Aineisto on oltava Excel taulukosta muodossa "Tabuloinnilla erotettu teksti (*.txt)" tallennettu tiedosto. Excel taulukon on noudatettava seuraavia sääntöjä:
   * Yhdessä taulukossa on aina yhden tositteen viennit. Vientien lukumäärä ei ole rajoitettu. Viennit ovat jokainen omalla rivillään ja vientejä edeltää sarakeotsikot sisältävä rivi.
   * Ennen tätä riviä tiedostossa saa olla mitä tahansa tietoa, jota ohjelma ei mitenkään käsittele. Sarakeotsikoilla voidaan kertoa tiedoston sisältävän seuraavia tietoja:
   * +--------------------------------------+-----------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------+
   * | Sarakeotsikko                        | Kuvaus                                                                                                                                                                                                          |
   * +--------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------+
   * | Tositelaji                           |Oltava perustettuna kirjanpitoon, annetaan ensimmäisellä vientirivillä. Jos tietoa ei ole, ohjelma käyttää oletustositelajia. Tositenumero määräytyy tositelajin perusteella kuten tositteita käsin syötettäessä.|                                                                       |
   * | Pvm tai Pv                           | Tositteen päiväys, annetaan ensimmäisellä vientirivillä. Päivämäärä anneteen muodossa ppkkvv. Pakollinen tieto.                                                                                                 |
   * | Selite tai Vientiselite              | Viennin selite. Tositekohtainen selite voidaan antaa rivillä, jolla ei anneta lainkaan tilinumeroa.                                                                                                             |
   * | Tili                                 | Tilinumero, pakollinen tieto (lukuun ottamatta tositeselitettä).                                                                                                                                                |
   * | Kp, Kust.paikka tai Kustannuspaikka  | Kustannuspaikka                                                                                                                                                                                                 |
   * | Tunniste tai Projekti                | Tunniste/projekti                                                                                                                                                                                               |
   * | Alv-menetelmä tai Alv-men            | Alv-menetelmä (käytössä vain tietyillä Tisma-käyttäjillä).                                                                                                                                                      |
   * | Debet, Debetvienti tai Debetsumma    | Debet viennin summa                                                                                                                                                                                             |
   * | Kredit, Kreditvienti tai Kreditsumma | Kredit viennin summa. Yhdellä rivillä ei voi olla sekä debet että kredit -vientiä. Jompi kumpi kuitenkin pitää olla.                                                                                            |
   * +--------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------+
   * Sarakeotsikoiden kirjoitusasu isojen ja pienten kirjainten suhteen on vapaa. Jos yllä on esitetty useampi kuin yksi kirjoitusasu, mikä tahansa näistä kelpaa.
   * Vähintään sarakkeet "Pvm", "Tili", "Debet" ja "Kredit"on aineistosta löydyttävä. Sarakkeiden keskinäinen järjestys on vapaa.
   * Jos tiedosto sisältää muitakin kuin kuvatut sarakkeet, ohjelma ei käsittele näitä mitenkään. Ohjelman käsittelemät tiedot täytyy sijaita 12 ensimmäisen sarakkeen joukossa.
   * Selitteiden merkistö pitää olla ISO-Latin merkistöä
   */
  private static getOscarSoftwareExportData(data: AccountingData): any[] {
    const result = [];
    const header = ["Tositelaji", "Pvm", "Selite", "Tili", "Kp", "Projekti", "Debet", "Kredit"];
    result.push(header);
    const entryDate = this.getEntryDate(data);
    for (const ledgerAccount of data.ledgerAccounts) {
      const keyEntriesRows: {
        /** Cost center */
        costCenter: string,
        /** ProjectNumber */
        projectNumber: string,
        /** Ledger entries */
        entries: LedgerEntry[],
      }[] = [];
      for (const ledgerEntry of ledgerAccount.entries) {
        const costCenter = ledgerEntry.dimension.costCenter || ledgerEntry.dimension.dimensions?.costCenter || "";
        const projectNumber = ledgerEntry.dimension.projectNumber || ledgerEntry.dimension.dimensions?.projectNumber || ledgerEntry.dimension.dimensions?.project || "";
        let keyEntryRows = keyEntriesRows.find((x) =>  x.costCenter == costCenter && x.projectNumber == projectNumber);
        if (keyEntryRows == null) {
          keyEntryRows = { costCenter, projectNumber, entries: [] };
          keyEntriesRows.push(keyEntryRows);
        }
        keyEntryRows.entries.push(ledgerEntry);
      }
      for (const keyEntryRows of keyEntriesRows) {
        const row = [];
        // Tositelaji
        row.push(null);
        // Pvm
        row.push(Dates.format(entryDate, "DDMMYY"));
        // Selite
        row.push(ledgerAccount.accountName);
        // Tili
        row.push(ledgerAccount.accountNumber);
        // Kp
        row.push(keyEntryRows.costCenter);
        // Projekti
        row.push(keyEntryRows.projectNumber);
        //Debet & Kredit
        const saldo = Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0));
        if (saldo >= 0) {
          // "Debet",
          row.push(saldo);
          // "Kredit",
          row.push(null);
        } else {
          // "Debet",
          row.push(null);
          // "Kredit",
          row.push(saldo * -1.0);
        }
        result.push(row);
      }
    }

    return result;
  }

  /**
   * Lemonsoft
   * https://doc.lemonsoft.eu/lemonnethelp/default.htm#!Documents/excelcsv.htm
   * +----------+--------------+---------------------------------------------------+
   * | Otsikko  |    Muoto     |                 Selite                            |
   * +----------+--------------+---------------------------------------------------+
   * | Tosite   | 0            | Vakio = 0                                         |
   * | Tlaji    | NUM          | Tositelaji                                        |
   * | Tnro     | NUM          | Tositenumero                                      |
   * | Pvm      | VVVV-KK-PP   | Päivämäärä muodossa VVVV-KK-PP (esim. 2009-10-20) |
   * | Selite   | AN(60)       | Vientiselite                                      |
   * | Kpaikka  | AN(40)       | Kustannuspaikka                                   |
   * | LTA      | AN(40)       | Liiketoiminta-alue                                |
   * | Tunniste | AN(30)       | Tunniste                                          |
   * | Projekti | NUM          | Projektinumero                                    |
   * | Tili     | AN(10)       | Kirjanpitotili                                    |
   * | Debet    | NUM          | Debet                                             |
   * | Kredit   | NUM          | Kredit                                            |
   * | Vero     | NUM          | Veron määrä                                       |
   * +----------+--------------+---------------------------------------------------+
   */
  private static getLemonsoftExportData(data: AccountingData): any[] {
    const result = [];
    const header = [
      "Tosite",
      "Tlaji",
      "Tnro",
      "Pvm",
      "Selite",
      "Kpaikka",
      "LTA",
      "Tunniste",
      "Projekti",
      "Tili",
      "Debet",
      "Kredit",
      "Vero"
    ];
    result.push(header);
    const entryDate = this.getEntryDate(data);
      for (const ledgerAccount of data.ledgerAccounts) {
        const keyEntriesRows: {
          /** Cost center */
          costCenter: string,
          /** ProjectNumber */
          projectNumber: string,
          /** Ledger entries */
          entries: LedgerEntry[],
        }[] = [];
        for (const ledgerEntry of ledgerAccount.entries) {
          const costCenter = ledgerEntry.dimension.costCenter || ledgerEntry.dimension.dimensions?.costCenter || "";
          const projectNumber = ledgerEntry.dimension.projectNumber || ledgerEntry.dimension.dimensions?.projectNumber || ledgerEntry.dimension.dimensions?.project || "";
          let keyEntryRows = keyEntriesRows.find((x) =>  x.costCenter == costCenter && x.projectNumber == projectNumber);
          if (keyEntryRows == null) {
            keyEntryRows = { costCenter, projectNumber, entries: [] };
            keyEntriesRows.push(keyEntryRows);
          }
          keyEntryRows.entries.push(ledgerEntry);
        }
        // Append results
        for (const keyEntryRows of keyEntriesRows) {
          const row = [];
          //Tosite
          row.push(0);
          //Tlaji
          row.push(null);
          //Tnro
          row.push(null);
          //Pvm
          row.push(Dates.asJSDate(entryDate));
          //Selite
          row.push(ledgerAccount.accountName);
          //Kpaikka
          row.push(keyEntryRows.costCenter);
          //LTA
          row.push(null);
          //Tunniste
          row.push(null);
          //Projekti
          row.push(keyEntryRows.projectNumber);
          //Tili
          row.push(ledgerAccount.accountNumber);
          //Debet && Kredit
          const saldo = Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0));
          if (saldo >= 0) {
            // "Debet",
            row.push(saldo);
            // "Kredit",
            row.push(0);
          } else {
            // "Debet",
            row.push(0);
            // "Kredit",
            row.push(saldo * -1.0);
          }
          //Vero
          row.push(Numeric.round(keyEntryRows.entries.reduce((a, b) => a + (b.vatAmount ?? 0), 0)));

          result.push(row);
        }
      }

    return result;
  }

  /**
   * | Nimi             | Tositepäivä             | Tositelaji | Tositenumero | Tili    | Alv-Koodi | Summa              | Kustannuspaikka | Laskentakohde1 | Laskentakohde2 | Laskentakohde3 | Laskentakohde4 | Laskentakohde5 | Laskentakohde6 | Laskentakohde7 | Laskentakohde8 | Laskentakohde9 | Laskentakohde10 | Laskentakohde11 | Laskentakohde12 | Laskunumero | Vientiselite |
   * |------------------|-------------------------|------------|--------------|---------|-----------|--------------------|-----------------|----------------|----------------|----------------|----------------|----------------|----------------|----------------|----------------|----------------|-----------------|-----------------|-----------------|-------------|--------------|
   * | Tyyppi           | Date                    | varchar    | biginit      | varchar | varchar   | desimal            | varchar         | varchar        | varchar        | varchar        | varchar        | varchar        | varchar        | varchar        | varchar        | varchar        | varchar         | varchar         | varchar         | varchar     | varchar      |
   * |                  | Päivämäärä              | Teksti     | Kokonaisluku |         |           | Luku, 2 desimaalia |                 |                |                |                |                |                |                |                |                |                |                 |                 |                 |             |              |
   * | Pituus           |                         | 10         | 20           | 6       | 10        | 13,2               | 30              | 30             | 30             | 30             | 30             | 30             | 30             | 30             | 30             | 30             | 30              | 30              | 30              | 30          | 100          |
   * | Muoto            | pp.kk.vvvv tai p.k.vvvv |            |              |         |           |                    |                 |                |                |                |                |                |                |                |                |                |                 |                 |                 |             |              |
   * | Pakollisuus      | V                       | V          | V            | P       | V         | P                  | V               | V              | V              | V              | V              | V              | V              | V              | V              | V              | V               | V               | V               | V           | V            |
   * | Järjestys        | 1.00                    | 2.00       | 3.00         | 4.00    | 5.00      | 6.00               | 7.00            | 8.00           | 9.00           | 10.00          | 11.00          | 12.00          | 13.00          | 14.00          | 15.00          | 16.00          | 17.00           | 18.00           | 19.00           | 20.00       | 21.00        |
   * | Sarake Excelissä | A                       | B          | C            | D       | E         | F                  | G               | H              | I              | J              | K              | L              | M              | N              | O              | P              | Q               | R               | S               | T           | U            |
   */

  private static getHeerosExportData(data: AccountingData, schemeId?: any): any[] {
    const result = [];
    const header = [
      "Tositepvm",
      "Tositelaji",
      "Tositenro",
      "Tili",
      "Alv-koodi",
      "Summa",
      "Kustp",
      "Proj",
      "Lask2",
      "Lask3",
      "Lask4",
      "Lask5",
      "Lask6",
      "Lask7",
      "Lask8",
      "Lask9",
      "Lask10",
      "Lask11",
      "Lask12",
      "LaskuNro",
      "Selite"
    ];
    result.push(header);

    if (schemeId === "extended") {
      for (const ledgerAccount of data.ledgerAccounts) {
        for (const ledgerEntry of ledgerAccount.entries) {
          const row = [];
          // Tositepvm
          row.push(Dates.asJSDate(ledgerEntry.entryDate));
          // Tositelaji
          row.push(null);
          // Tositenro
          row.push(null);
          // Tili
          row.push(ledgerAccount.accountNumber);
          // Alv-koodi
          row.push(ledgerEntry.vatPercent ?
            ((ledgerEntry.vatPercent * 100 === 24 || ledgerEntry.vatPercent * 100 === 25.5) ? 1 :
              (ledgerEntry.vatPercent * 100 === 14) ? 2 :
                (ledgerEntry.vatPercent * 100 === 10) ? 3 :
                  "") : "");
          // Summa
          row.push(Numeric.round(ledgerEntry.isDebit ? ledgerEntry.amount : -1.0 * ledgerEntry.amount));
          // kustp
          row.push(ledgerEntry.dimension.costCenter || ledgerEntry.dimension.dimensions?.costCenter || "");
          // Proj
          row.push(ledgerEntry.dimension.projectNumber || ledgerEntry.dimension.dimensions?.projectNumber || ledgerEntry.dimension.dimensions?.project || "");
          // Lask2
          row.push(null);
          // Lask3
          row.push(null);
          // Laske4
          row.push(null);
          // Lask5
          row.push(null);
          // Lask6
          row.push(null);
          // Lask7
          row.push(null);
          // Lask8
          row.push(null);
          // Lask9
          row.push(null);
          // Lask10
          row.push(null);
          // Lask11
          row.push(null);
          // Lask12
          row.push(null);
          // LaskuNro
          row.push(null);
          // Selite
          row.push(ledgerEntry.description);
          result.push(row);
        }
      }
    }
    else
    {
      const entryDate = this.getEntryDate(data);
      for (const ledgerAccount of data.ledgerAccounts) {
        const keyEntriesRows: {
          /** VAT */
          vatPercent: number,
          /** Cost center */
          costCenter: string,
          /** ProjectNumber */
          projectNumber: string,
          /** Ledger entries */
          entries: LedgerEntry[],
        }[] = [];
        for (const ledgerEntry of ledgerAccount.entries) {
          const vatPercent = ledgerEntry.vatPercent ?? 0;
          const costCenter = ledgerEntry.dimension.costCenter || ledgerEntry.dimension.dimensions?.costCenter || "";
          const projectNumber = ledgerEntry.dimension.projectNumber || ledgerEntry.dimension.dimensions?.projectNumber || ledgerEntry.dimension.dimensions?.project || "";
          let keyEntryRows = keyEntriesRows.find((x) => x.vatPercent == vatPercent && x.costCenter == costCenter && x.projectNumber == projectNumber);
          if (keyEntryRows == null) {
            keyEntryRows = { vatPercent, costCenter, projectNumber, entries: [] };
            keyEntriesRows.push(keyEntryRows);
          }
          keyEntryRows.entries.push(ledgerEntry);
        }
        // Append results
        for (const keyEntryRows of keyEntriesRows) {
          const row = [];
          // Tositepvm
          row.push(Dates.asJSDate(entryDate));
          // Tositelaji
          row.push(null);
          // Tositenro
          row.push(null);
          // Tili
          row.push(ledgerAccount.accountNumber);
          // Alv-koodi
          row.push(keyEntryRows.vatPercent ?
            ((keyEntryRows.vatPercent * 100 === 24 || keyEntryRows.vatPercent * 100 === 25.5) ? 1 :
              (keyEntryRows.vatPercent * 100 === 14) ? 2 :
                (keyEntryRows.vatPercent * 100 === 10) ? 3 :
                  "") : "");
          // Summa
          const saldo = Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0));
          row.push(saldo);
          // Kustp
          row.push(keyEntryRows.costCenter);
          // Proj
          row.push(keyEntryRows.projectNumber);
          // Lask2
          row.push(null);
          // Lask3
          row.push(null);
          // Laske4
          row.push(null);
          // Lask5
          row.push(null);
          // Lask6
          row.push(null);
          // Lask7
          row.push(null);
          // Lask8
          row.push(null);
          // Lask9
          row.push(null);
          // Lask10
          row.push(null);
          // Lask11
          row.push(null);
          // Lask12
          row.push(null);
          // LaskuNro
          row.push(null);
          // Selite
          row.push(ledgerAccount.accountName);
          result.push(row);
        }
      }
    }
    return result;
  }

  /*
  Url: https://community.visma.com/t5/Visma-Fivaldi-kayttovinkit/Visma-Fivaldi-Kasikirja-RAJAPINNAT/ta-p/35780?attachment-id=11454 - Sivu 66
  PVM Pakollinen N8 Kirjauspäivä tositteelle, muoto VVVVKKPP
  TILI Pakollinen AN8 Kirjanpidon tili
  TOSITE AN16 Tositenumero jolla tositteet erotetaan. Jos tositenumeroa ei ole, päiväyksen muuttuminen vaihtaa seuraavaan tositteeseen
  SK1 AN8 Seurantakohde 1
  SK2 AN8 Seurantakohde 2
  SK3 AN8 Seurantakohde 3
  SK4 AN8 Seurantakohde 4
  SUMMA Pakollinen N15,2 Summa yrityksen valuutassa etumerkkeineen
  SENTIT N15 Summa sentteinä etumerkkeineen
  SELITE AN179 Vientiselite
  VEROKANTA AN10 Verokantakoodi
  VEROTILI AN8 Verotili
  VEROTILI2 AN8 Verotili 2 (vastakirjaus)
  VEROSUMMA N15,2 Veron määrä yrityksen valuutassa etumerkkeineen
  VEROSENTIT N15 Veron määrä sentteinä etumerkkeineen
  STATUS AN1 Viennin erikoiskäsittely
  NETTOPVM N8 Laskun eräpäivä VVVVKKPP
  ASIAKASTUNNUS AN10 Asiakkaan/toimittajan tunnus
  VIENTILAJI AN2 Vientilajin tunnus
  LASKUNO AN16 Laskun numero
  YT N6 Fivaldi-yritysnumero jolle aineisto tuodaan
  LAJI N3 Tositelajin numero jolle aineisto tuodaan
  */
  private static getVismaFivaldiExportData(data: AccountingData): any[] {
    const result = [];
    const header = [
      "PVM",
      "TILI",
      "TOSITE",
      "SK1",
      "SK2",
      "SK3",
      "SK4",
      "SUMMA",
      "SENTIT",
      "SELITE",
      "VEROKANTA",
      "VEROTILI",
      "VEROTILI2",
      "VEROSUMMA",
      "VEROSENTIT",
      "STATUS",
      "NETTOPVM",
      "ASIAKASTUNNUS",
      "VIENTILAJI",
      "LASKUNO",
      "YT",
      "LAJI",
    ];

    result.push(header);

    const entryDate = this.getEntryDate(data);
    for (const ledgerAccount of data.ledgerAccounts) {
      const keyEntriesRows: {
        /** Cost center */
        costCenter: string,
        /** Ledger entries */
        entries: LedgerEntry[],
      }[] = [];
      for (const ledgerEntry of ledgerAccount.entries) {
        const costCenter = ledgerEntry.dimension.costCenter || ledgerEntry.dimension.dimensions?.costCenter || "";
        let keyEntryRows = keyEntriesRows.find((x) => x.costCenter == costCenter);
        if (keyEntryRows == null) {
          keyEntryRows = { costCenter, entries: [] };
          keyEntriesRows.push(keyEntryRows);
        }
        keyEntryRows.entries.push(ledgerEntry);
      }
      // Append results
      for (const keyEntryRows of keyEntriesRows) {
        const row = [];
        //PVM
        row.push(Dates.format(entryDate, "YYYYMMDD", null));
        //TILI
        row.push(ledgerAccount.accountNumber);
        //TOSITE
        row.push(null);
        //SK1
        row.push(keyEntryRows.costCenter);
        //SK2
        row.push(null);
        //SK3
        row.push(null);
        //SK4
        row.push(null);
        const saldo = Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0));
        //SUMMA
        row.push(saldo);
        //SENTIT
        row.push(saldo * 100);
        //SELITE
        row.push(ledgerAccount.accountName);
        //VEROKANTA
        row.push(null);
        //VEROTILI
        row.push(null);
        //VEROTILI2
        row.push(null);
        //VEROSUMMA
        const vat = Numeric.round(keyEntryRows.entries.reduce((a, b) => a + (b.isDebit ? (b.vatAmount ?? 0) : -1.0 * (b.vatAmount ?? 0)), 0));
        row.push(vat);
        //VEROSENTIT
        row.push(vat * 100);
        //STATUS
        row.push(null);
        //NETTOPVM
        row.push(null);
        //ASIAKASTUNNUS
        row.push(null);
        //VIENTILAJI
        row.push(null);
        //LASKUNO
        row.push(null);
        //YT
        row.push(null);
        //LAJI
        row.push(null);

        result.push(row);
      }
    }
    return result;
  }
  /**
   * Tampuuri
   * https://eteinen.tampuuri.fi/s/artikkelit?article=Tuonti-Excelista
   * +--------------------------+-------------------+----------------------------------------------------------------------------------------------------------------------------------------------------------------+
   * | Sarakeotsikko            |    Pakollinen     |                 Kuvaus                                                                                                                                         |
   * +---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------+
   * | Tositenro                | Ei                | Mikäli tositenumeroa ei anna, järjestelmä generoi sen tositelajin laskurin   mukaan.                                                                           |
   * | Pvm                      | Kyllä             | Tositepäivä.                                                                                                                                                   |
   * | Tositeselite             | Kyllä             | Tositeotsikon selite                                                                                                                                           |
   * | Tili                     | Kyllä             | Kirjanpidon tili                                                                                                                                               |
   * | Alvluokka                | Ei                | Arvolisäverokoodi, esim 024                                                                                                                                    |
   * | Alvvähennysoikeus pros   | Ei                | Mikäli tositerivillä olevasta arvolisäverosta voidaan vain osa   vähentää, tässä annetaan vähennysoikeusprosentti                                              |
   * | Vientiselite             | Ei                | Tositerivin selite.                                                                                                                                            |
   * | Debet                    | (Kyllä)           | Viennille annettava debet-summa                                                                                                                                |
   * | Kredit                   | (Kyllä)           | Viennille annettava kredit-summa (positiivisena, kuten tositteen   syötössä). Joko debet- tai -kredit kentässä on oltava arvo.                                 |
   * | Tositelaji               | Ei                | Käytetty tositelaji, esimerkiksi Muistiotosite. Mikäli tietoa ei anna,   tositelajina käytetään sitä tositelajia, jonka tyypiksi on   merkitty Tositeliittymä. |
   * +---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------+
   */
  private static getTampuuriExportData(data: AccountingData): any[]  {
    const result = [];
    const header = ["Tositenro", "Pvm", "Tositeselite", "Tili", "Alvluokka", "Alvvähennysoikeus pros", "Vientiselite", "Debet", "Kredit", "Tositelaji"];
    const dimensionHeaders = [];

    const addToGlobal = (value, name) => {
      if (value) {
        if (!dimensionHeaders.includes(name)) {
          dimensionHeaders.push(name);
          header.push(name);
        }
      }
    };
    // Find all dimensions
    for (const rowData of data.ledgerAccounts) {
      for (const entryData of rowData.entries) {
        const costCenter = entryData.dimension.costCenter || entryData.dimension.dimensions?.costCenter;
        addToGlobal(costCenter, "costCenter");
        const projectNumber = entryData.dimension.projectNumber || entryData.dimension.dimensions?.projectNumber;
        addToGlobal(projectNumber, "projectNumber");
        if (entryData.dimension.dimensions) {
          for (const [key, value] of Object.entries(entryData.dimension.dimensions)) {
            addToGlobal(value, key);
          }
        }
      }
    }
    result.push(header);
    const entryDate = this.getEntryDate(data);

    for (const ledgerAccount of data.ledgerAccounts) {
      const keyEntriesRows: {
        /** Entries */
        entries: LedgerEntry[],
        /** VAT */
        vatPercent: number,
        /** Dimensions */
        dimensions: any[],
        /** DimensionsId */
        dimensionsId: string
      }[] = [];
      for (const entryData of ledgerAccount.entries) {
        const rowDataEntriesDimensions = [];
        for (const dimensionHeader of dimensionHeaders) {
          if (dimensionHeader === "costCenter") {
            const costCenter = entryData.dimension.costCenter || entryData.dimension.dimensions?.costCenter || "";
            rowDataEntriesDimensions.push(costCenter);
          }
          else if (dimensionHeader === "projectNumber") {
            const projectNumber = entryData.dimension.projectNumber || entryData.dimension.dimensions?.projectNumber || "";
            rowDataEntriesDimensions.push(projectNumber);
          } else {
            rowDataEntriesDimensions.push(entryData.dimension.dimensions ? (entryData.dimension.dimensions[dimensionHeader] || "") : "");
          }
        }
        const vatPercent = entryData.vatPercent ?? 0;
        let keyEntryRows: {
          /** Entries */
          entries: LedgerEntry[],
          /** VAT */
          vatPercent: number,
          /** Dimensions */
          dimensions: any[],
          /** DimensionsId */
          dimensionsId: string
        } = keyEntriesRows.find((x) => x.vatPercent === vatPercent && x.dimensionsId === rowDataEntriesDimensions.join("-"));
        if (keyEntryRows == null) {
          keyEntryRows = { entries: [], vatPercent, dimensions: rowDataEntriesDimensions, dimensionsId: rowDataEntriesDimensions.join("-") };
          keyEntriesRows.push(keyEntryRows);
        }
        keyEntryRows.entries.push(entryData);
      }
      // Append results
      for (const keyEntryRows of keyEntriesRows) {
        const row = [];
        //Tositenro
        row.push(null);
        //Pvm
        row.push(Dates.asJSDate(entryDate));
        //Tositeselite
        row.push("PALKAT");
        //Tili
        row.push(ledgerAccount.accountNumber);
        //Alvluokka
        row.push(keyEntryRows.vatPercent ?  Numeric.toFixed(1000 + (keyEntryRows.vatPercent * 100),0).substring(1) : null);
        //Alvvähennysoikeus pros
        row.push(null);
        //Vientiselite
        row.push(ledgerAccount.accountName);
        //Debet & Kredit
        const saldo = Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0));
        if (saldo >= 0) {
          // "Debet",
          row.push(saldo);
          // "Kredit",
          row.push(0);
        } else {
          // "Debet",
          row.push(0);
          // "Kredit",
          row.push(saldo * -1.0);
        }
        //Tositelaji"
        row.push(null);

        for (let i = 0; i < dimensionHeaders.length; i++) {
          row.push(keyEntryRows.dimensions[i]);
        }
        result.push(row);
      }
    }
    return result;
  }


  private static getDigiaExportData(data: AccountingData): any[] {
    const result = [];
    const header = ["Kirjauspäivä", "Tositelaji", "Tositeno", "Kp-tili", "DebetKredit", "Rahamäärä", "Valuuttakoodi", "Valuuttakurssi", "Selite", "Alv", "Tulosyksikkö", "Seurantakohde1", "Seurantakohde2", "Seurantakohde3", "Seurantakohde4", "Seurantakohde5", "Seurantakohde6", "Seurantakohde7", "Seurantakohde8", "Seurantakohde9"];
    result.push(header);
    const entryDate = this.getEntryDate(data);

    for (const ledgerAccount of data.ledgerAccounts) {
      const keyEntriesRows: {
        /** VAT */
        vatPercent: number,
        /** Cost center */
        costCenter: string,
        /** ProjectNumber */
        projectNumber: string,
        /** Ledger entries */
        entries: LedgerEntry[],
      }[] = [];
      for (const ledgerEntry of ledgerAccount.entries) {
        const vatPercent = ledgerEntry.vatPercent ?? 0;
        const costCenter = ledgerEntry.dimension.costCenter || ledgerEntry.dimension.dimensions?.costCenter || "";
        const projectNumber = ledgerEntry.dimension.projectNumber || ledgerEntry.dimension.dimensions?.projectNumber || ledgerEntry.dimension.dimensions?.project || "";
        let keyEntryRows = keyEntriesRows.find((x) => x.vatPercent == vatPercent && x.costCenter == costCenter && x.projectNumber == projectNumber);
        if (keyEntryRows == null) {
          keyEntryRows = { vatPercent, costCenter, projectNumber, entries: [] };
          keyEntriesRows.push(keyEntryRows);
        }
        keyEntryRows.entries.push(ledgerEntry);
      }
      // Append results
      for (const keyEntryRows of keyEntriesRows) {

        const row = [];
        // Kirjauspäivä
        row.push(Dates.asJSDate(entryDate));
        // TositeLaji
        row.push(null);
        // Tositeno
        row.push(null);
        // Kp-tili
        row.push(ledgerAccount.accountNumber);
        // DebetKredit
        const saldo = Numeric.round(keyEntryRows.entries.reduce((sum, x) => sum + (x.isDebit ? x.amount : -1.0 * x.amount), 0));
        row.push(saldo >= 0 ? "+" : "-");
        // Rahamäärä
        row.push(saldo < 0 ? saldo * -1.0 : saldo);
        // Valuuttakoodi
        row.push("EUR");
        // Valuuttakurssi
        row.push(1);
        // Selite
        row.push(ledgerAccount.accountName);
        // Alv
        row.push(keyEntryRows.vatPercent * 100);
        // Tulosyksikkö
        row.push(null);
        // Seurantakohde1
        row.push(keyEntryRows.costCenter);
        // Seurantakohde 2
        row.push(keyEntryRows.projectNumber);
        // Seurantakohde 3
        row.push(null);
        // Seurantakohde 4
        row.push(null);
       // Seurantakohde 5
        row.push(null);
        // Seurantakohde 6
        row.push(null);
        // Seurantakohde 7
        row.push(null);
        // Seurantakohde 8
        row.push(null);
        // Seurantakohde 9
        row.push(null);

        result.push(row);
      }
    }

    return result;
  }

  private static formatAsDigiaText(data: any[], _isFinnish: boolean): string {
    const textRows = [];
    data = data.slice(1);
    for (const row of data) {
      row[0] = this.padRight(row[0] ? Dates.format(row[0], "DDMMYY") : null,6);
      row[1] = this.padRight(row[1],4);
      row[2] = this.padLeft(row[2],8);
      row[3] = this.padRight(row[3],8);
      row[4] = this.padLeft(row[4],1);
      row[5] = this.padLeft(Numeric.toString(Numeric.round(row[5] * 100,0)),11, "0");
      row[6] = this.padRight(row[6],3);
      row[7] = this.padLeft(Numeric.toString(Numeric.round(row[7] * 1000000000,0)),17, "0");
      row[8] = this.padRight(row[8],30);
      row[9] = this.padLeft(null,6);
      row[10] = this.padRight(row[10],3);
      row[11] = this.padRight(row[11],8);
      row[12] = this.padRight(row[12],8);
      row[13] = this.padRight(row[13],8);
      row[14] = this. padRight(row[14],8);
      row[15] = this.padRight(row[15],8);
      row[16] = this.padRight(row[16],8);
      row[17] = this.padRight(row[17],8);
      row[18] = this.padRight(row[18],8);
      row[19] = this.padRight(row[19],8);

      textRows.push(row.join(""));
    }
    return textRows.join("\r\n");
  }

  private static padRight(value: string, length: number, padChar = " "): string {
    const txt = value ?? "";
    if (txt.length > length) {
      return txt.substring(0, length);
    }
    return txt + padChar.repeat(length - txt.length);
  }

  private static padLeft(value: string, length: number, padChar = " "): string {
    const txt = value ?? "";
    if (txt.length > length) {
      return txt.substring(0, length);
    }
    return padChar.repeat(length - txt.length) + txt;
  }

  private static getEntryDate(data: AccountingData): string {
    // Return the period end, if given
    if (data.period?.end) {
      return data.period.end;
    }
    // Try to find the max entry date
    let entryDate = null;
    for (const ledgerAccount of data.ledgerAccounts) {
      for (const ledgerEntry of ledgerAccount.entries) {
        if (ledgerEntry.entryDate) {
          if (!entryDate || Dates.isAfter(ledgerEntry.entryDate, entryDate)) {
            entryDate = ledgerEntry.entryDate;
          }
        }
      }
    }
    return entryDate;
  }
}